import React, { useState, useEffect } from "react";
import { onAuthChange, getCurrentUser } from "../firebase/auth";
import { getDocumentById, updateDocument } from "../firebase/firestore";
import { db } from "../firebase/config";
import { doc, onSnapshot } from "firebase/firestore";
import AuthContext from "./AuthContext";

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let profileUnsub = null;
        const unsubscribe = onAuthChange(async (authUser) => {
            try {
                if (profileUnsub) {
                    profileUnsub();
                    profileUnsub = null;
                }
                if (authUser) {
                    setUser(authUser);
                    // Live subscribe to user profile for real-time updates (e.g., dashboardUnlocked)
                    profileUnsub = onSnapshot(doc(db, "users", authUser.uid), (snap) => {
                        if (snap.exists()) {
                            setUserProfile({ id: snap.id, ...snap.data() });
                        } else {
                            setUserProfile(null);
                        }
                        setLoading(false);
                    }, (err) => {
                        console.error("Profile subscription error:", err);
                        setError(err.message);
                        setLoading(false);
                    });
                } else {
                    setUser(null);
                    setUserProfile(null);
                    setLoading(false);
                }
            } catch (err) {
                console.error("Error in auth state change:", err);
                setError(err.message);
                setLoading(false);
            }
        });

        return () => {
            if (profileUnsub) profileUnsub();
            unsubscribe();
        };
    }, []);

    const logout = async () => {
        try {
            const { logoutUser } = await import("../firebase/auth");
            await logoutUser();
            setUser(null);
            setUserProfile(null);
            setError(null);
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const updateUserProfile = async (updates) => {
        if (!user?.uid) {
            throw new Error("No user logged in");
        }

        try {
            await updateDocument("users", user.uid, {
                ...updates,
                updatedAt: new Date()
            });

            setUserProfile((prev) => ({
                ...prev,
                ...updates,
                updatedAt: new Date()
            }));

            return true;
        } catch (err) {
            console.error("Error updating user profile:", err);
            throw new Error(`Failed to update profile: ${err.message}`);
        }
    };

    const value = {
        user,
        userProfile,
        loading,
        error,
        logout,
        updateUserProfile,
        isAdmin: userProfile?.role === "admin",
        isStudent: userProfile?.role === "student",
        isStartup: userProfile?.role === "startup"
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
